import React from "react";
import PropTypes from "prop-types";

import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { FAM_V2_ADD, FAM_V2_EDIT } from "./events";
import RelativeForm, { safeRelationshipLabel } from "./RelativeForm";

/**
 * Family-history editor rail — a STACK of write sheets, newest on top.
 *
 * All of the stack behaviour (the blank-sheet guard, one-sheet-per-record,
 * serialised saves, the refusal pulse, the live region) lives in
 * ../common/useSheetStack + ../common/SheetStack, which every v3 rail shares.
 * This file is now only the section's wiring: which events open a sheet, how
 * to pull a relative out of an edit payload, and what to call each sheet.
 * The form itself — every field, rule and write — is RelativeForm.
 *
 * Every label that names a record goes through safeRelationshipLabel: a
 * relationship arriving object-shaped would otherwise reach JSX (the live
 * region renders `notice` directly) and take the whole patient screen down.
 */

const ADD = [FAM_V2_ADD];
const EDIT = [FAM_V2_EDIT];

const getRecord = (payload) => payload && payload.relative;
const getRecordId = (r) => r && r.relativeId;
const describeRecord = (r) => safeRelationshipLabel(r) || "relative";
const dupMessage = (r) => `${describeRecord(r)} is already open for update.`;

const RelativeEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    addEvents: ADD,
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank relative form is already open.",
    dupMessage,
    enabled: !!patientId,
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="fh3-editor"
      describe={(s) => (s.editing ? `Edit ${describeRecord(s.editing)}` : "Add relative")}
    >
      {(sheet, props) => <RelativeForm patientId={patientId} {...props} />}
    </SheetStack>
  );
};

RelativeEditor.propTypes = {
  patient: PropTypes.object,
};

export default RelativeEditor;
